import React, { useEffect, useRef } from 'react';
import { formatNumber } from '@/utils/formatters';

/**
 * SupporterBadgeModal
 *
 * Gamified donation CTA that fires when the player's score lands in the
 * top 10% of the daily leaderboard (or exceeds a local threshold).
 *
 * Design principles:
 * - Appears ONLY after game over, never during play
 * - Tied to a genuine achievement ("You're in the top 10%!")
 * - Single clear CTA — no dark patterns, easy to dismiss
 * - Vanilla inline styles for zero bundle impact
 */

interface SupporterBadgeModalProps {
  score: number;
  percentile: number; // 0–100 (lower = better rank, e.g. 5 = top 5%)
  onClose: () => void;
}

export function SupporterBadgeModal({ score, percentile, onClose }: SupporterBadgeModalProps) {
  const closeRef = useRef<HTMLButtonElement>(null);

  // Auto-focus close button for keyboard accessibility
  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  // Dismiss on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const tierLabel =
    percentile <= 1
      ? '🏆 Top 1% — Elite Runner!'
      : percentile <= 5
        ? '⭐ Top 5% — Champion!'
        : '🎖️ Top 10% — High Scorer!';

  const handleTipClick = () => {
    if (typeof window !== 'undefined' && (window as any).trackTipJarClick) {
      (window as any).trackTipJarClick('supporter_badge_modal');
    }
    window.open('https://tiptopjar.com/chromedinogame', '_blank', 'noopener,noreferrer');
    onClose();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="supporter-badge-title"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // z-70: above SystemMenu (z-40), Share modal (z-50), and GameOverAdBanner
        zIndex: 70,
        padding: '16px',
        animation: 'fadeIn 200ms cubic-bezier(0.23, 1, 0.32, 1)',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: scale(0.95) translateY(8px); } to { opacity: 1; transform: scale(1) translateY(0); } }
        @media (prefers-reduced-motion: reduce) {
          .supporter-card { animation: none !important; }
        }
      `}</style>

      <div
        className="supporter-card"
        style={{
          background: 'var(--bg-color)',
          borderRadius: '12px',
          padding: '28px 24px',
          maxWidth: '360px',
          width: '100%',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          textAlign: 'center',
          fontFamily: 'system-ui, -apple-system, Roboto, Arial, sans-serif',
          animation: 'slideUp 220ms cubic-bezier(0.23, 1, 0.32, 1)',
          position: 'relative',
        }}
      >
        {/* Close button */}
        <button
          ref={closeRef}
          onClick={onClose}
          aria-label="Close"
          style={{
            position: 'absolute',
            top: '12px',
            right: '14px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: '18px',
            color: '#999',
            lineHeight: 1,
            padding: '4px',
          }}
        >
          ×
        </button>

        {/* Badge icon */}
        <div style={{ fontSize: '48px', marginBottom: '8px' }}>🦕</div>

        {/* Achievement label */}
        <div
          style={{
            display: 'inline-block',
            background: 'linear-gradient(135deg, #1557b0, #1a73e8)',
            color: '#ffffff',
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '0.08em',
            padding: '4px 12px',
            borderRadius: '20px',
            marginBottom: '14px',
            textTransform: 'uppercase',
          }}
        >
          {tierLabel}
        </div>

        {/* Headline */}
        <h2
          id="supporter-badge-title"
          style={{
            fontSize: '20px',
            fontWeight: 700,
            color: '#1a1a1a',
            margin: '0 0 8px',
            lineHeight: 1.3,
          }}
        >
          Incredible Run!
        </h2>

        {/* Score display */}
        <p style={{ fontSize: '32px', fontWeight: 800, color: '#1a73e8', margin: '0 0 10px', fontFamily: 'monospace' }}>
          {formatNumber(score)}
        </p>

        {/* Value proposition */}
        <p style={{ fontSize: '13px', color: '#555', lineHeight: 1.5, margin: '0 0 20px' }}>
          You just outscored <strong>{100 - percentile}%</strong> of today's players.
          If you're enjoying the game, a small tip keeps the leaderboard running for everyone.
        </p>

        {/* Primary CTA */}
        <button
          onClick={handleTipClick}
          style={{
            display: 'block',
            width: '100%',
            padding: '13px 20px',
            background: 'linear-gradient(135deg, #1557b0, #1a73e8)',
            color: '#ffffff',
            border: 'none',
            borderRadius: '8px',
            fontSize: '15px',
            fontWeight: 700,
            cursor: 'pointer',
            marginBottom: '10px',
            transition: 'opacity 0.15s ease',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
        >
          🫙 Leave a Tip
        </button>

        {/* Dismiss link */}
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            color: '#999',
            fontSize: '12px',
            cursor: 'pointer',
            padding: '4px',
            textDecoration: 'underline',
          }}
        >
          No thanks, keep playing
        </button>
      </div>
    </div>
  );
}
